import React from 'react';
import { AUTO_FIT_ROW, autoFitItem, normalizeColumns } from '../../utils/autoFitRow';
import { Button } from '@/components/ui/button';
import {
  ArrowUpDown,
  ExternalLink,
  Tag,
  MapPin,
  Calendar,
  Gauge,
  Car,
} from 'lucide-react';
import { ExtendedVehicle } from './VehicleGrid';
import { Badge } from '@/components/ui/badge';
import { VehicleCard } from './VehicleCard';
import { VehicleCard2 } from './VehicleCard2';
import { SimpleVehicle } from './VehicleCarousel';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@iconify/react';
import { useTranslation } from '@/i18n/hooks/useTranslation';

// 👉 Framer Motion (ligero)
import { LazyMotion, domAnimation, m, useReducedMotion, type Variants } from 'framer-motion';
import {
  compararPorOrden,
  ordenDelTenant,
  type OrdenVehiculos,
} from '@/lib/orden-vehiculos';
import useClientStore from '@/store/useClientStore';
import { precioModoDelCliente } from '@/lib/precio-web';

interface VehicleListProps {
  vehicles: ExtendedVehicle[];
  columns: 2 | 3 | 4;
  getStatusColor: (status: string) => string;
  // Ticket #164: el vocabulario de orden vive en `lib/orden-vehiculos` —
  // incluye 'default', que es el orden INICIAL que configuró la automotora.
  // No se ofrece en el desplegable (como antes el default, que tampoco
  // estaba): con él, el botón muestra su rótulo genérico sin marcar nada.
  // Antes el vocabulario estaba repetido a mano acá y en las dos grillas.
  sortOrder: OrdenVehiculos;
  setSortOrder: React.Dispatch<React.SetStateAction<OrdenVehiculos>>;
  cardSettings?: {
    cardBgColor: string;
    cardBorderColor: string;
    cardTextColor: string;
    cardPriceColor: string;
    cardButtonColor: string;
    cardButtonTextColor: string;
    detailsButtonText: string;
    bannerPosition: 'left' | 'right';
    pricePosition: 'overlay' | 'below-title';
    featuresConfig?: {
      feature1?: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature2?: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature3?: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
      feature4?: 'category' | 'year' | 'fuel' | 'mileage' | 'transmission';
    };
  }[];
  newBadgeText?: string;
  /** usa Card2 cuando proviene del grid2 */
  source?: 'grid' | 'grid2';
}

// `key` es el VALOR del orden; la etiqueta visible sale del i18n (site.vehicles.sort*).
const sortOptions = [
  { key: 'date_desc', labelKey: 'site.vehicles.sortDateDesc', icon: 'mdi:clock-outline' },
  { key: 'date_asc', labelKey: 'site.vehicles.sortDateAsc', icon: 'mdi:clock' },
  { key: 'price_asc', labelKey: 'site.vehicles.sortPriceAsc', icon: 'mdi:sort-ascending' },
  { key: 'price_desc', labelKey: 'site.vehicles.sortPriceDesc', icon: 'mdi:sort-descending' },
  { key: 'year_desc', labelKey: 'site.vehicles.sortYearDesc', icon: 'mdi:calendar' },
  { key: 'year_asc', labelKey: 'site.vehicles.sortYearAsc', icon: 'mdi:calendar-outline' },
  { key: 'mileage_asc', labelKey: 'site.vehicles.sortMileageAsc', icon: 'mdi:speedometer-slow' },
];

export const VehicleList: React.FC<VehicleListProps> = ({
  vehicles,
  columns,
  getStatusColor,
  sortOrder,
  setSortOrder,
  cardSettings,
  newBadgeText,
  source = 'grid',
}) => {
  const { t } = useTranslation();
  // Ticket #164: el orden inicial que configuró la automotora. Hace falta acá
  // porque este componente es el que ordena al final, y `'default'` significa
  // "el orden de la automotora", no "no ordenar".
  const { client } = useClientStore();
  const ordenTenant = ordenDelTenant((client as Record<string, unknown> | null)?.web_vehicle_order);
  // Ticket #171: cómo muestran las tarjetas los dos precios (ver `lib/precio-web`).
  const precioModo = precioModoDelCliente(client);
  const ordenActual = sortOptions.find((o) => o.key === sortOrder);
  // Fila que se centra: una automotora con 1 o 2 autos publicados dejaba el
  // resto de las columnas vacías y el catálogo pegado a la izquierda.
  const numColumns = normalizeColumns(columns, 4);
  const itemClass = autoFitItem(
    numColumns <= 2
      ? [{ cols: 1 }, { bp: 'sm', cols: 2 }]
      : numColumns === 3
        ? [{ cols: 1 }, { bp: 'sm', cols: 2 }, { bp: 'md', cols: 3 }]
        : [{ cols: 1 }, { bp: 'sm', cols: 2 }, { bp: 'md', cols: 3 }, { bp: 'lg', cols: 4 }],
  );

  // Ticket #164: vendidos y reservados al final, y DENTRO de cada nivel el
  // comparador único del sitio (`lib/orden-vehiculos`).
  //
  // Acá había un `switch` repetido —el tercero— y era el que tenía la última
  // palabra: las dos grillas ya ordenan con `ordenarVehiculos` y después
  // pasaban la lista por este. Con `default: return 0` el orden de la
  // automotora sobrevivía de casualidad, por la estabilidad de `Array.sort`;
  // pero con autos de una carga masiva (mismo `created_at`) las ramas de fecha
  // también devolvían 0 y la elección del visitante se perdía acá, después de
  // haberse aplicado bien más arriba.
  const comparar = compararPorOrden(sortOrder, ordenTenant);
  const sortedVehicles = [...vehicles].sort((a, b) => {
    if (a.status?.name === 'Vendido' && b.status?.name !== 'Vendido') return 1;
    if (a.status?.name !== 'Vendido' && b.status?.name === 'Vendido') return -1;
    if (a.status?.name === 'Reservado' && b.status?.name !== 'Reservado') return 1;
    if (a.status?.name !== 'Reservado' && b.status?.name === 'Reservado') return -1;
    return comparar(a, b);
  });

  // ===== Animaciones (suaves y eficientes)
  const reduce = useReducedMotion();

  const containerVariants = {
    hidden: { opacity: 1 }, // mantenemos visible para evitar reflujo
    show: {
      opacity: 1,
      transition: {
        // Stagger + delayChildren muy leves
        staggerChildren: 0.06,
        delayChildren: 0.04,
      },
    },
  } as const;

  // `Variants`: con framer-motion 12.34 el `ease` en array tiene que tiparse
  // como tupla; los valores no cambian.
  const itemVariants: Variants = reduce
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0.15 } },
      }
    : {
        hidden: { opacity: 0, y: 8, scale: 0.98 },
        show: {
          opacity: 1,
          y: 0,
          scale: 1,
          transition: {
            duration: 0.35,
            ease: [0.22, 1, 0.36, 1], // similar a easeOutExpo suave
          },
        },
      };

  return (
    <div className='w-full'>
      {/* Sort Controls */}
      <div className='flex justify-end mb-4'>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              size='sm'
              className='flex items-center gap-1 text-xs border border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
            >
              <span>{(ordenActual ? t(ordenActual.labelKey) : '') || t('site.vehicles.sortBy')}</span>
              <Icon icon='mdi:sort' className='text-base' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {sortOptions.map((option) => (
              <DropdownMenuItem
                key={option.key}
                onSelect={() => setSortOrder(option.key as any)}
                className={sortOrder === option.key ? 'font-bold bg-gray-100 text-black' : ''}
              >
                <Icon icon={option.icon} className='text-sm mr-2' />
                {t(option.labelKey)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {vehicles.length === 0 ? (
        <div className='text-center p-6 bg-gray-50 rounded-lg'>
          <p className='text-gray-500'>{t('site.vehicles.noMatches')}</p>
        </div>
      ) : (
        <LazyMotion features={domAnimation}>
          <m.div
            variants={containerVariants}
            initial='hidden'
            animate='show'
            className={`${AUTO_FIT_ROW} gap-6 [--bg-gap:1.5rem]`}
          >
            {sortedVehicles.map((vehicle) => {
              const features = cardSettings?.[0]?.featuresConfig;
              const common = {
                compact: false,
                cardBgColor: cardSettings?.[0]?.cardBgColor,
                cardBorderColor: cardSettings?.[0]?.cardBorderColor,
                cardTextColor: cardSettings?.[0]?.cardTextColor,
                cardPriceColor: cardSettings?.[0]?.cardPriceColor,
                cardButtonColor: cardSettings?.[0]?.cardButtonColor,
                cardButtonTextColor: cardSettings?.[0]?.cardButtonTextColor,
                detailsButtonText: cardSettings?.[0]?.detailsButtonText,
                bannerPosition: cardSettings?.[0]?.bannerPosition,
                pricePosition: cardSettings?.[0]?.pricePosition,
                newBadgeText,
                precioModo,
              } as const;

              return (
                <m.div key={vehicle.id} variants={itemVariants} className={itemClass}>
                  {source === 'grid2' ? (
                    <VehicleCard2
                      vehicle={vehicle as unknown as SimpleVehicle}
                      {...common}
                    />
                  ) : (
                    <VehicleCard
                      vehicle={vehicle as unknown as SimpleVehicle}
                      {...common}
                      featuresConfig={features}
                    />
                  )}
                </m.div>
              );
            })}
          </m.div>
        </LazyMotion>
      )}
    </div>
  );
};
